import { T, useGT } from 'gt-react';
import type { HarnessRecommendation, PageKey } from '@erp/rpc';
import { useHarnessRecommendations } from '@/api/rpc/hooks/harness/useHarnessToolCalls';
import { cn } from '@/lib/utils';

const SEVERITY_DOT: Record<HarnessRecommendation['severity'], string> = {
  urgent: 'bg-destructive',
  warn: 'bg-amber-500',
  info: 'bg-muted-foreground/60',
};

/**
 * Page-aware suggested actions shown while the thread is empty. Each card is
 * computed server-side through the user's own permissions; clicking one sends
 * its prompt as the user's message, like a suggestion chip.
 */
export function NamiRecommendedActions({
  pageKey,
  entityId,
  onSelect,
  disabled = false,
}: {
  pageKey: PageKey | undefined;
  entityId?: string;
  onSelect: (prompt: string) => void;
  disabled?: boolean;
}) {
  const gt = useGT();
  const { data, isLoading } = useHarnessRecommendations(pageKey, entityId);
  const recommendations = data?.recommendations ?? [];
  if (!pageKey || isLoading || recommendations.length === 0) return null;
  return (
    <section
      aria-label={gt('Recommended actions', {
        context:
          'accessible name of the recommended actions list in the AI assistant panel',
      })}
      className="mb-2 flex flex-col gap-1.5"
    >
      <h3 className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        <T context="heading above suggested actions in the AI assistant panel">
          Recommended actions
        </T>
      </h3>
      {recommendations.map((item) => (
        <button
          key={item.id}
          type="button"
          disabled={disabled}
          onClick={() => onSelect(item.prompt)}
          className={cn(
            'flex w-full items-start gap-2 rounded-md border bg-background px-2.5 py-2 text-left text-xs hover:bg-muted/40 disabled:opacity-60',
          )}
        >
          <span
            className={cn(
              'mt-1 size-2 shrink-0 rounded-full',
              SEVERITY_DOT[item.severity],
            )}
            aria-hidden
          />
          <span className="min-w-0">
            <span className="block font-medium text-foreground">
              {item.title}
            </span>
            {item.detail ? (
              <span className="block text-muted-foreground">{item.detail}</span>
            ) : null}
          </span>
        </button>
      ))}
    </section>
  );
}
